import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { 
  RefreshCw, 
  Calendar, 
  DollarSign, 
  Pause, 
  Play,
  X,
  Settings,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const SubscriptionManager = ({ onBack }) => {
  const [subscriptions, setSubscriptions] = useState([
    {
      id: 'sub_1',
      description: 'Monthly rent payment',
      amount: 500.00,
      currency: 'USD',
      interval: 'month',
      intervalCount: 1,
      status: 'active',
      nextPayment: '2024-08-25',
      recipient: {
        name: 'David Kim',
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=50&h=50&fit=crop&crop=face'
      },
      group: 'Roommates',
      createdAt: '2024-07-25'
    },
    {
      id: 'sub_2',
      description: 'Weekly office lunch contribution',
      amount: 25.00,
      currency: 'USD',
      interval: 'week',
      intervalCount: 1,
      status: 'active',
      nextPayment: '2024-07-30',
      recipient: {
        name: 'Sarah Chen',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=50&h=50&fit=crop&crop=face'
      },
      group: 'Office Lunch',
      createdAt: '2024-07-20'
    },
    {
      id: 'sub_3',
      description: 'Utility bills share',
      amount: 75.00,
      currency: 'USD',
      interval: 'month',
      intervalCount: 1,
      status: 'paused',
      nextPayment: '2024-09-01',
      recipient: {
        name: 'Mike Torres',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face'
      },
      group: 'Apartment Expenses',
      createdAt: '2024-06-15'
    }
  ]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getIntervalDisplay = (interval, count) => {
    const plural = count > 1 ? 's' : '';
    if (count > 1) {
      return `Every ${count} ${interval}${plural}`;
    }
    return `${interval.charAt(0).toUpperCase() + interval.slice(1)}ly`;
  };

  const handlePauseResume = (subscriptionId) => {
    setSubscriptions(prev => prev.map(sub => {
      if (sub.id === subscriptionId) {
        const newStatus = sub.status === 'active' ? 'paused' : 'active';
        toast.success(`Subscription ${newStatus === 'active' ? 'resumed' : 'paused'} successfully`);
        return { ...sub, status: newStatus };
      }
      return sub;
    }));
  };

  const handleCancel = (subscriptionId) => {
    setSubscriptions(prev => prev.filter(sub => sub.id !== subscriptionId));
    toast.success('Subscription cancelled successfully');
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'default',
      paused: 'secondary',
      cancelled: 'destructive'
    };
    
    const colors = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800'
    };

    return (
      <Badge variant={variants[status]} className={colors[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const activeSubscriptions = subscriptions.filter(sub => sub.status === 'active');
  const totalMonthlyAmount = activeSubscriptions.reduce((sum, sub) => {
    // Convert all to monthly equivalent for calculation
    let monthlyAmount = sub.amount;
    if (sub.interval === 'week') {
      monthlyAmount = sub.amount * 4.33; // Average weeks per month
    } else if (sub.interval === 'year') {
      monthlyAmount = sub.amount / 12;
    }
    return sum + (monthlyAmount / sub.intervalCount);
  }, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center space-x-4">
          <Button variant="ghost" onClick={onBack} size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Manage Subscriptions</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Active Subscriptions</p>
                  <p className="text-2xl font-bold text-gray-900">{activeSubscriptions.length}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <RefreshCw className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Monthly Total</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(totalMonthlyAmount)}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Next Payment</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {activeSubscriptions.length > 0 
                      ? formatDate(Math.min(...activeSubscriptions.map(sub => new Date(sub.nextPayment))))
                      : 'No active subscriptions'
                    }
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscriptions List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl font-semibold">Your Subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            {subscriptions.length === 0 ? (
              <div className="text-center py-12">
                <RefreshCw className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Subscriptions</h3>
                <p className="text-gray-500 mb-6">You don't have any recurring payment subscriptions set up yet.</p>
                <Button onClick={onBack} className="bg-blue-600 hover:bg-blue-700">
                  Create First Subscription
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {subscriptions.map((subscription) => (
                  <div 
                    key={subscription.id}
                    className="p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={subscription.recipient.avatar} alt={subscription.recipient.name} />
                          <AvatarFallback>{subscription.recipient.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-1">
                            <h3 className="font-semibold text-gray-900">{subscription.description}</h3>
                            {getStatusBadge(subscription.status)}
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <span>To: {subscription.recipient.name}</span>
                            <span>•</span>
                            <span>Group: {subscription.group}</span>
                            <span>•</span>
                            <span>{getIntervalDisplay(subscription.interval, subscription.intervalCount)}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Created: {formatDate(subscription.createdAt)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-6">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">
                            {formatCurrency(subscription.amount)}
                          </p>
                          <p className="text-sm text-gray-500">
                            Next: {formatDate(subscription.nextPayment)}
                          </p>
                        </div>

                        <div className="flex items-center space-x-2">
                          {subscription.status === 'active' ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePauseResume(subscription.id)}
                              className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
                            >
                              <Pause className="w-4 h-4 mr-1" />
                              Pause
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePauseResume(subscription.id)}
                              className="text-green-600 border-green-200 hover:bg-green-50"
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Resume
                            </Button>
                          )}
                          
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-gray-600 border-gray-200 hover:bg-gray-50"
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancel(subscription.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionManager;